/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

/**
 * Client types for the medaica-service pcg_review wire contract.
 *
 * AUTHORITATIVE SOURCE: medaica-service `app/pcg_review/schemas.py` +
 * `export_validation.py`. These types mirror that implementation
 * field-for-field (camelCase per APIResponseModel; ISO datetimes revived to
 * Date by the portal's shared axios interceptor; UUID string PKs). Section
 * references (§) in this file are to the handoff's third-party evidence spec
 * (`pcg_exam_viewer_third_party_evidence_spec.md`).
 *
 * Contract highlights:
 * - Disposition wire field is `decision` (accepted|rejected|none); `none` +
 *   note is the spec §11 blue note-only state; toggling a decision off
 *   writes a `none` row (note may remain); absence of rows = never reviewed.
 *   No `hasNote` anywhere; no server composite `exportValue` — the client is
 *   the single owner of the spec §11 composite (deriveDispositionExportValue).
 * - Reject reasons on non-rejected PUTs are accepted but silently dropped.
 * - Guide rows are `ok|error`; ABSENCE of a row (`guide: null`) = pending
 *   (matches the worker's NOT-EXISTS work marker).
 * - Rows join `reviewerDisplayName`; exports join `exporterDisplayName`
 *   (the server's field names are `version`/`exporterId`) and stamp
 *   `payloadSchemaVersion`.
 * - Export POST structurally validates the spec §13.3 payload and 422s with
 *   {"error": {message, schemaVersion, errors[]}} BEFORE storing anything;
 *   the payload must contain at least one recognized section
 *   (viewerReview|medaicaEvidence|evidence|recordings|thirdPartyEvidence)
 *   and any `recordings[*]` entry must carry `recordingId` (ADR 009).
 *
 * Endpoints (all behind ensure_permission; writes additionally require an
 * HCP user):
 *   GET  /api/v1/exams/{examId}/pcg-review                          → PcgReviewBundle
 *   PUT  /api/v1/exams/{examId}/auscultations/{auscultationId}/disposition
 *        PcgDispositionRequest → PcgRecordingDisposition
 *   POST /api/v1/exams/{examId}/pcg-review/export (201)
 *        PcgExportRequest → PcgReviewExport (full row incl. payload)
 *   GET  /api/v1/exams/{examId}/pcg-review/exports                  → PcgReviewExportSummary[]
 *   GET  /api/v1/exams/{examId}/pcg-review/exports/{version}        → PcgReviewExport
 */

import { Exam } from "@medaica/common/types"

// --- Guide (analysis pipeline output, auscultation_guide table) ---

/**
 * Row states are `ok|error`; there is NO `pending` value — a recording whose
 * guide has not been produced yet has `guide: null` in the bundle.
 */
type PcgGuideStatus = "ok" | "error"

type PcgGuide = {
  id: string
  /** Pipeline + model version stamp, e.g. "pcg-guide-1.0.0+s1s2-3/turb-2". */
  guideVersion: string
  status: PcgGuideStatus
  /**
   * Raw pipeline payload (traces/markers/gate/metrics — the prototype's
   * __DATA__ shape, schema per extracted/sample-__DATA__-schema.txt).
   * Returned verbatim — the server stores guides without interpreting them;
   * the client's pcg-core derives tiers/timing/energy evidence from the
   * payload. Typed opaquely here — pcg-core owns the schema.
   * Present when status is "ok"; null on "error".
   */
  payload: unknown
  errorDetail: string | null
  dateCreated: Date | null
}

// --- Dispositions (spec §11/§12) ---

/** Export enums from spec §12, table "Reject Reasons". */
type PcgRejectReason =
  | "signal_quality"
  | "timing_unreliable"
  | "s1s2_guide_unreliable"
  | "sys_dia_guide_unreliable"
  | "structural_sound_evidence_unreliable"
  | "wrong_site_or_mislabeled_position"
  | "third_party_mismatch"
  | "too_few_usable_cycles"
  | "other"

/**
 * "none" = no Accept/Reject decision. A row with decision "none" and a
 * non-empty note is the spec's blue "note without decision" state; toggling
 * an active decision off writes a "none" row (note may remain — spec §11).
 * Absence of any row = never reviewed (grey).
 */
type PcgDispositionDecision = "accepted" | "rejected" | "none"

// --- Structured reviewer observations (handoff v0.8.3.1) ---

/** Primary screening observation — observational language by design. */
type PcgObservationFinding = "murmur_suspected" | "no_murmur_suspected" | "unsure"

/** Observed acoustic pattern; only meaningful with "murmur_suspected". */
type PcgObservationPattern =
  | "systolic_ejection"
  | "holosystolic"
  | "decrescendo_systolic"
  | "early_diastolic"
  | "mid_diastolic"
  | "late_diastolic"
  | "continuous"
  | "timing_indeterminate"

type PcgOtherAcousticObservation = "click_suspected" | "rub_like_sound" | "artifact_or_non_cardiac"

type PcgRhythmObservation = "irregular_rhythm" | "rate_unreliable" | "extra_heart_sound"

/** The reviewer's own rating of how well the heart sounds can be heard (the note dialog's "HCP acoustic rating"). */
type PcgAcousticQuality = "clear" | "weak" | "not_usable"

/** Inclusive bounds of a reviewer-confirmed heart rate; the server enforces the same. */
const CONFIRMED_BPM_MIN = 25
const CONFIRMED_BPM_MAX = 220

/**
 * A confirmed heart rate as the export and the report use it: a whole number
 * within bounds, else null (an out-of-range or non-numeric value counts as
 * not entered).
 */
const normalizeConfirmedBpm = (value: unknown): number | null => {
  if (value === null || value === undefined || value === "") {
    return null
  }
  const bpm = Number(value)
  return Number.isFinite(bpm) && bpm >= CONFIRMED_BPM_MIN && bpm <= CONFIRMED_BPM_MAX ? Math.round(bpm) : null
}

/**
 * Structured screening observations saved with a disposition row (one
 * "Reviewer observations" save writes decision + note + observations
 * together). The server normalizes permissively (§12 style): a pattern
 * without finding "murmur_suspected" is dropped, and an all-empty object is
 * stored as null — so a row's `observations` is null or has content.
 *
 * The selections and the disposition's free-text note are internal HCP
 * review context; `reportNote` is the only wording that reaches the patient
 * and provider reports.
 */
type PcgReviewerObservations = {
  /** Absent on rows saved before the rating existed. */
  acousticQuality?: PcgAcousticQuality | null
  /** Whole number 25–220; absent on rows saved before it existed. */
  confirmedBpm?: number | null
  finding: PcgObservationFinding | null
  pattern: PcgObservationPattern | null
  otherObservations: PcgOtherAcousticObservation[]
  rhythmObservations: PcgRhythmObservation[]
  /** Report-facing wording for this recording; absent on rows saved before it existed. */
  reportNote?: string | null
}

/** True when the observations object carries at least one selection or a Report note. */
const observationsHaveContent = (observations: PcgReviewerObservations | null | undefined): boolean =>
  Boolean(
    observations &&
      (observations.acousticQuality ||
        normalizeConfirmedBpm(observations.confirmedBpm) !== null ||
        observations.finding ||
        observations.pattern ||
        observations.otherObservations.length > 0 ||
        observations.rhythmObservations.length > 0 ||
        (observations.reportNote ?? "").trim().length > 0)
  )

/**
 * Spec §11 composite value. Deliberately NOT on the wire — the server stays
 * dumb; the client derives it (deriveDispositionExportValue) for display and
 * for the export payload it builds (export_validation pins this vocabulary
 * for viewerReview.disposition).
 */
type PcgDispositionExportValue =
  | "unreviewed"
  | "accepted"
  | "accepted_with_note"
  | "rejected"
  | "rejected_with_note"
  | "noted"

/**
 * The single client-side owner of the §11 composite derivation (UI badges
 * and pcg-core export payload rows must agree by construction). Since the
 * v0.8.3.1 structured-observations patch, "reviewer content" means a
 * free-text note OR any structured selection — either one makes the
 * *_with_note / noted composite.
 */
const deriveDispositionExportValue = (
  decision: PcgDispositionDecision,
  note: string | null,
  observations: PcgReviewerObservations | null = null
): PcgDispositionExportValue => {
  const hasNote = (note ?? "").trim().length > 0 || observationsHaveContent(observations)
  if (decision === "accepted") {
    return hasNote ? "accepted_with_note" : "accepted"
  }
  if (decision === "rejected") {
    return hasNote ? "rejected_with_note" : "rejected"
  }
  return hasNote ? "noted" : "unreviewed"
}

/**
 * Which evidence domains of an accepted recording feed the exam-level Heart
 * Report. At least one is true on an accepted row that carries domains.
 */
type PcgAcceptedDomains = {
  timing: boolean
  structuralSound: boolean
}

type PcgPluginReviewStatus = "unreviewed" | "accepted" | "rejected" | "noted"

/** The reviewer's Accept / Reject / Note on one plugin's result for one recording. */
type PcgPluginReview = {
  status: PcgPluginReviewStatus
  note: string | null
}

/**
 * Plugin reviews saved with the recording's disposition — information for
 * the reviewer only; they never change the decision or what feeds the report.
 * A plugin is null when never reviewed.
 */
type PcgPluginReviews = {
  act: PcgPluginReview | null
  beataware: PcgPluginReview | null
}

type PcgDispositionRequest = {
  decision: PcgDispositionDecision
  /**
   * Meaningful only with decision "accepted" (dropped otherwise). Omitted or
   * null on an accepted row means both domains.
   */
  acceptedDomains?: PcgAcceptedDomains | null
  /** Meaningful only with decision "rejected"; silently dropped otherwise (§12, permissive). */
  rejectReasons: PcgRejectReason[]
  note: string
  /** Structured observations saved with the note; null clears them. */
  observations: PcgReviewerObservations | null
  /**
   * Omitted: the current plugin reviews are kept. Sent: they are replaced
   * (null clears them).
   */
  pluginReviews?: PcgPluginReviews | null
}

/**
 * Append-only history row: a PUT never updates in place, it supersedes
 * (revalidation creates a new row, never updates).
 */
type PcgRecordingDisposition = {
  id: string
  auscultationId: string
  decision: PcgDispositionDecision
  /** Null on an accepted row = both domains (rows saved before domains existed). */
  acceptedDomains?: PcgAcceptedDomains | null
  rejectReasons: PcgRejectReason[] | null
  note: string | null
  observations: PcgReviewerObservations | null
  /** Absent from servers that predate plugin reviews. */
  pluginReviews?: PcgPluginReviews | null
  reviewerId: string
  reviewerDisplayName: string | null
  dateCreated: Date | null
}

/**
 * The "reviewer observations" content test used everywhere a note used to be
 * tested: blue noted-state dots, the Note button's active state, and export
 * inclusion all mean "free text OR structured selections".
 */
const dispositionHasReviewerContent = (disposition: PcgRecordingDisposition | null | undefined): boolean =>
  Boolean(
    disposition && ((disposition.note ?? "").trim().length > 0 || observationsHaveContent(disposition.observations))
  )

// --- Export (spec §13; payload is built client-side by pcg-core, stored verbatim) ---

type PcgExportRequest = {
  /**
   * The §13.3 evidence package built client-side (pcg-core export builder +
   * page-level exam envelope). Structurally validated server-side
   * (export_validation.py, permissive-but-structural: at least one
   * recognized section; recordings[*].recordingId required when present;
   * viewerReview.disposition must use the §11 vocabulary) then stored
   * verbatim as an immutable versioned row.
   */
  payload: unknown
}

type PcgReviewExport = {
  id: string
  examId: string
  /** Monotonic per exam, assigned server-side. (Server field name: `version`.) */
  version: number
  /** The export_validation schema version that admitted the payload, e.g. "medaica.pcg.review-export-schema.v2". */
  payloadSchemaVersion: string
  exporterId: string
  exporterDisplayName: string | null
  dateCreated: Date | null
  /**
   * Server-computed digest of the review state (dispositions, guides) the
   * export snapshot was taken from. Used server-side to de-duplicate
   * exports; the UI does not interpret it.
   */
  reviewFingerprint: string | null
  payload: unknown
}

/**
 * Export POST result: the stored export plus whether this call created it.
 * The server de-duplicates — when the review has not changed since the
 * latest export it returns that export with `created: false` instead of a
 * new version, so the client may post freely whenever it needs a current
 * snapshot.
 */
type PcgReviewExportResult = PcgReviewExport & { created: boolean }

/** Bundle/list shape — payloads are large; fetch one by version for the full row. */
type PcgReviewExportSummary = Omit<PcgReviewExport, "payload">

/** Body of the export POST's 422 (under the house {"error": {...}} envelope). */
type PcgExportValidationErrorBody = {
  message: string
  schemaVersion: string
  errors: string[]
}

// --- Medaica Report versions (immutable version-per-save; optimistic concurrency on the base version) ---

/**
 * Lifecycle status stored with each saved report version. A report with no
 * saved version yet is "preliminary" — a client-side label only, never
 * stored. Saving "approved" or "released" marks the exam as reviewed; saving
 * "in_review" on top of either (a reopen / revert to draft) clears that flag
 * again. "released" is reachable only from "approved" (or "released"); the
 * server answers any other transition with a 422.
 */
type PcgHeartReportStatus = "in_review" | "approved" | "released"

type PcgHeartReportSaveRequest = {
  /** The whole report document, stored verbatim (pcg-report core owns the shape). */
  payload: unknown
  /**
   * Optimistic concurrency: the report version this edit session loaded
   * (null = started fresh from an export, no report rows expected). A
   * mismatch with the server's latest is a 409 — re-load, don't clobber.
   */
  baseVersion: number | null
  /** Which review-export version the report was derived from (provenance). */
  sourceExportVersion: number
  status: PcgHeartReportStatus
  /**
   * The approval attestation the clinician confirmed. Required on every save
   * that approves the report (the server checks it against its own wording
   * and refuses anything else with a 422); a release revert is the one
   * "approved" save without it.
   */
  approvalStatement?: string
  /**
   * The patient-facing document, built client-side by the same helpers the
   * patient view renders from. Required on a "released" save: the server
   * freezes it (renders the PDF once, stores it, emails the patient a link)
   * in the same transaction as the version row. Ignored for other statuses.
   */
  patientDocument?: PcgPatientReportDocument
  /**
   * The clinician-format provider report as data, built client-side by the
   * same helpers the provider view renders from. Sent with a "released" save
   * beside the patient document: the server freezes it as a second PDF the
   * patient can download to share with their own doctor — so everything in
   * it is patient-readable once released. Ignored for other statuses.
   */
  providerDocument?: PcgProviderReportDocument
}

type PcgHeartReport = {
  id: string
  examId: string
  /** Monotonic per exam, assigned server-side. */
  version: number
  payloadSchemaVersion: string
  sourceExportVersion: number | null
  status: PcgHeartReportStatus
  editorId: string
  editorDisplayName: string | null
  dateCreated: Date | null
  payload: unknown
}

/**
 * The latest saved Medaica Report version as summarized in the review bundle,
 * with the server's verdict on whether the review has moved on since the
 * export snapshot that version was derived from.
 */
type PcgHeartReportBundleSummary = {
  status: PcgHeartReportStatus
  version: number
  sourceExportVersion: number | null
  dateCreated: Date | null
  editorDisplayName: string | null
  reviewChangedSinceSource: boolean
  /** The active patient release (delivery facts for the status line); null unless released. */
  release: PcgHeartReportRelease | null
}

/** Delivery facts of the active release, as the bundle and the resend endpoint report them. */
type PcgHeartReportRelease = {
  releasedAt: Date
  version: number
  /** The recipient address, masked ("j•••@example.com"). */
  recipientMasked: string
  /** Outbox status of the latest report-ready email: queued | sending | sent | delivered | failed; null if none. */
  emailStatus: "queued" | "sending" | "sent" | "delivered" | "failed" | null
  lastSentAt: Date | null
  linkExpiresAt: Date | null
  /**
   * Whether the release froze a provider report beside the patient report.
   * False for a release made without a provider document.
   */
  hasProviderReport: boolean
}

/** List shape — payloads are whole report documents; fetch one by version. */
type PcgHeartReportSummary = Omit<PcgHeartReport, "payload">

/** Body of the save POST's 409 (under the house {"error": {...}} envelope). */
type PcgHeartReportConflictBody = {
  message: string
  latestVersion: number | null
  baseVersion: number | null
}

// --- Patient report (temporary PDF rendering of a released Medaica Report) ---

/**
 * The traffic-light scale of a result card, as the patient view draws it,
 * flattened so the server-side PDF can paint the same picture without the
 * stylesheet: the marker dot position, the track gradient's colour stops
 * (left to right), the zone labels with the width each occupies, and the
 * reference-range line when the card shows one.
 */
type PcgPatientReportScale = {
  /** Marker dot position, percent from the left edge of the track (0-100); the range midpoint on a range card. */
  markerPct: number
  /** A heart-rate range: a marker at each end joined by a band; null for a single-value card. */
  rangePct: { minPct: number; maxPct: number } | null
  /** Linear-gradient stops, left to right; color is "#rrggbb", pct 0-100. */
  gradient: { color: string; pct: number }[]
  /** Zone labels in order; basisPct is each label's width in percent. */
  labels: { text: string; basisPct: number }[]
  /** The reference-range line under the scale, or null when the card shows none. */
  referenceText: string | null
}

/**
 * One result card of the patient-facing report, as the patient view shows
 * it: the derived status as colour + label, the observed value only where
 * the patient view shows a numeric, the patient wording, and the
 * traffic-light scale.
 */
type PcgPatientReportCard = {
  title: string
  statusLabel: string
  statusColor: "green" | "amber" | "red" | "grey"
  observed: string | null
  /** The "What this means" body text of the card. */
  explanation: string
  /** The card's info (ⓘ) help text, a tooltip on screen. */
  info: string | null
  /** The short result wording shown beside the card title. */
  result: string
  /** The card's traffic-light scale, or null for a card that renders none. */
  scale: PcgPatientReportScale | null
}

/**
 * The patient-facing report content, built client-side from a released
 * Medaica Report with the same rules the patient view renders with, and
 * posted to POST /exams/{examId}/heart-report/patient-report.pdf, which
 * answers with the rendered PDF (409 unless the latest report is released).
 */
/**
 * One Report note of the patient report: the recording label
 * ("Recording 1 · Aortic") and the reviewer's Report note, rendered by the
 * PDF as a white inner panel like the on-screen section.
 */
type PcgPatientReportNote = {
  label: string
  text: string
}

type PcgPatientReportDocument = {
  patientName: string
  examDate: string
  summaryText: string | null
  cards: PcgPatientReportCard[]
  recordingNotes: PcgPatientReportNote[]
  /** The qualification line under the notes heading; null (the Report notes section has none). */
  recordingNoteInstruction: string | null
  followUpText: string | null
  limitationText: string | null
  emergencyText: string | null
}

// --- Provider report (the clinician-format document frozen beside the patient report at release) ---

/**
 * One finding of the provider document, as the provider view prints it: the
 * title, the result line's parts (provider wording, reference range, flag),
 * the review-required line when the finding carries one, the interpretation
 * paragraph, and the reviewer's clinician note when they wrote one.
 */
type PcgProviderReportFinding = {
  title: string
  /** The provider result wording; null when the finding prints none. */
  resultText: string | null
  /** The reference range; printed in parentheses after the result, bare without one. */
  referenceText: string | null
  /** The flag after the result line (a normal finding carries none). */
  flagText: string | null
  /** The whole "Review required — …" line; null unless the finding needs review. */
  reviewText: string | null
  interpretation: string
  /** The reviewer-edited note to the doctor, printed as "Clinician note:"; null when not edited. */
  clinicianNote: string | null
}

/**
 * The patient questionnaire as the provider view shows it: the summary
 * flags (red first) and the answered questions by section.
 */
type PcgProviderReportQuestionnaire = {
  flags: { text: string; answer: string; level: string }[]
  /** Answered items only; sections left without any are dropped. */
  sections: { title: string; items: { question: string; answer: string }[] }[]
}

/**
 * The provider report content, built client-side from a Medaica Report with
 * the same rules the provider view renders with (questionnaire, reviewer
 * observations, then the prose document), for the server to render as a PDF
 * without access to the report rules.
 */
type PcgProviderReportDocument = {
  title: string
  /** The caveat printed for a patient reading a clinician-format report. */
  audienceText: string | null
  patientName: string
  examDate: string
  dob: string | null
  age: string | null
  sex: string | null
  patientId: string | null
  /** Null when the provider view shows no questionnaire panel. */
  questionnaire: PcgProviderReportQuestionnaire | null
  recordingNotes: PcgPatientReportNote[]
  /** The qualification line under the notes heading; null (the Report notes section has none). */
  recordingNoteInstruction: string | null
  findings: PcgProviderReportFinding[]
  /** The line printed in place of the findings when there are none; null otherwise. */
  findingsEmptyText: string | null
  summaryText: string
  recommendationsText: string | null
  /** The "Reviewing Clinician Notes" section; null when the reviewer wrote none. */
  clinicianNotesText: string | null
  disclaimerText: string
}

// --- Exam preview evidence (_exam_preview_evidence port — the one derivation that runs server-side) ---

type PcgRecordingPreviewEvidence = {
  timing: boolean
  soi: boolean
  /** An energy-ready review range is available; servers that predate it report `soi` only. */
  energy?: boolean
  usable: boolean
  score: number | null
  timingBasis?: unknown
  soiBasis?: unknown
  gateStatus?: string | null
  reason?: string | null
}

/** Keyed by auscultation id — the platform's stable recording key. */
type PcgExamPreviewEvidence = {
  recordings: { [auscultationId: string]: PcgRecordingPreviewEvidence }
  panelCandidates: { [auscultationId: string]: string[] }
  recommendedAuscultationId: string | null
}

// --- The single-load bundle (GET /exams/{id}/pcg-review) ---

/**
 * The anatomical site of a recording, derived by the server from the raw
 * auscultation point (h1 = aortic, h2 = pulmonic, h3 = tricuspid, h4 =
 * mitral — the positions of the capture diagram's heart dots; lung points
 * and `other` keep their platform names). `code` is stable for grouping and
 * layout; `label` is what users see.
 */
type PcgAuscultationSite = {
  code: string
  label: string
}

/** The four heart-site codes, in capture order. */
const HEART_SITE_CODES = ["aortic", "pulmonic", "tricuspid", "mitral"] as const
type HeartSiteCode = (typeof HEART_SITE_CODES)[number]

const isHeartSiteCode = (code: string | null | undefined): code is HeartSiteCode =>
  (HEART_SITE_CODES as readonly string[]).includes(code ?? "")

/**
 * AusculThing (ACT) result the heartbeat service stored for one recording:
 * -1 processing error, 0 insufficient data, 1 normal, 2 abnormal.
 */
type PcgActResult = {
  diagnosis: number
}

type PcgBeatAwareStatus = "processing" | "ready" | "failed" | "error"

/**
 * BeatAware result the heartbeat service stored for one recording.
 * `statistics` is BeatAware's per-segment list with its own keys (e.g.
 * "Heart Rate (BPM)", "Systole Duration (ms)" as "310 +/- 4"), present only
 * for a ready report that could be read.
 */
type PcgBeatAwareResult = {
  status: PcgBeatAwareStatus
  statistics: { [key: string]: unknown }[] | null
}

/** Third-party results for one recording; a source is null when it never processed the recording. */
type PcgVendorEvidence = {
  act: PcgActResult | null
  beataware: PcgBeatAwareResult | null
}

type PcgReviewRecordingBundle = {
  auscultationId: string
  /**
   * Raw platform vocabulary (h1..h4 for heart points, l1.. for lung points,
   * `other`). Kept on the wire for audit; never shown to users — display the
   * site label instead (recordingSiteLabel).
   */
  auscultationPoint: string | null
  /** Server-derived anatomical site; null when the point is unmapped. */
  site: PcgAuscultationSite | null
  /** Seconds. (Server field name: `recordingDuration`.) */
  recordingDuration: number | null
  /** Raw guide row, or null while the pipeline has not produced one (= pending). */
  guide: PcgGuide | null
  /** Latest disposition row, or null if never reviewed. */
  disposition: PcgRecordingDisposition | null
  /** Stored ACT / BeatAware results; absent from servers that predate the field. */
  vendorEvidence?: PcgVendorEvidence
}

type PcgReviewBundle = {
  examId: string
  examDateCreated: Date | null
  examNotes: string | null
  /**
   * Patient-reported questionnaire context (D2C-scoped: exam ← appointment
   * ← questionnaire entry), in the
   * prototype display shape ({completedDate, sections:[{id, title,
   * items:[{q, a}]}]}). Null is the NORMAL case: non-D2C exams,
   * appointments without entries, skipped questionnaires.
   */
  questionnaire: unknown
  /**
   * Server-computed exam-level preview evidence (_exam_preview_evidence
   * port — the one exam-level derivation with no JS twin, so it stays
   * server-side while everything else is derived client-side by pcg-core).
   */
  examPreviewEvidence: PcgExamPreviewEvidence | null
  exports: PcgReviewExportSummary[]
  /** Latest saved Medaica Report version, or null when none has been saved yet. */
  heartReport: PcgHeartReportBundleSummary | null
  /**
   * Heart-point recordings only, in exam capture order (Exam.auscultations
   * date_created order — the platform has no site ordering).
   */
  recordings: PcgReviewRecordingBundle[]
}

// --- Client contract (implemented by PcgReviewApi and the view-layer mock) ---

/**
 * The exam-review page programs against this contract; a build/runtime
 * switch picks the real axios client (medaicaApiService.pcgReview) or the
 * in-browser mock. getReviewBundle takes the loaded Exam (not just the id)
 * only because the MOCK fabricates its recording list from it — the real
 * client uses exam.id alone, and the parameter narrows to `examId: string`
 * when the mock retires.
 */
interface PcgReviewApiContract {
  getReviewBundle(exam: Exam): Promise<PcgReviewBundle>
  putDisposition(
    examId: string,
    auscultationId: string,
    request: PcgDispositionRequest
  ): Promise<PcgRecordingDisposition>
  /**
   * Resolves to the FULL stored row (incl. payload) plus `created`: false
   * when the server de-duplicated against the latest export (200) instead
   * of storing a new version (201).
   */
  postExport(examId: string, request: PcgExportRequest): Promise<PcgReviewExportResult>
  getExports(examId: string): Promise<PcgReviewExportSummary[]>
  getExport(examId: string, version: number): Promise<PcgReviewExport>
  /** Resolves to the FULL stored row; throws PcgHeartReportConflictError on a stale
   * baseVersion (409, nothing stored). */
  saveHeartReport(examId: string, request: PcgHeartReportSaveRequest): Promise<PcgHeartReport>
  getHeartReportVersions(examId: string): Promise<PcgHeartReportSummary[]>
  getHeartReportVersion(examId: string, version: number): Promise<PcgHeartReport>
  /**
   * The frozen patient artifact of the active release — the same bytes the
   * patient downloads. Throws PcgPatientReportNotReleasedError (409) when the
   * report is not released.
   */
  getPatientReportPdf(examId: string): Promise<Blob>
  /**
   * The frozen provider report of the active release — the same bytes the
   * patient downloads to share with their own doctor. Throws
   * PcgPatientReportNotReleasedError (409) when the report is not released;
   * a 404 API error when the release carries no provider report.
   */
  getProviderReportPdf(examId: string): Promise<Blob>
  /**
   * Queue the report-ready email again with a fresh link. Throws
   * PcgPatientReportNotReleasedError (409) when not released; a RATE_LIMITED
   * (429) API error within a minute of the last send.
   */
  resendPatientReportEmail(examId: string): Promise<PcgHeartReportRelease>
  /**
   * A stored analysis-model artifact (e.g. the serialized turbulent-energy
   * forest) by its version — the value a guide payload names in
   * `turbulentModelVersion`. Immutable per version; the JSON is the model's
   * own schema (snake_case keys), returned verbatim.
   */
  getModelArtifact(version: string): Promise<Record<string, unknown>>
}

/**
 * The name a recording is displayed under: the server-derived site label,
 * else the raw point for rows that predate the site field, else "unlabeled".
 */
const recordingSiteLabel = (recording: {
  site?: PcgAuscultationSite | null
  auscultationPoint: string | null
}): string => recording.site?.label ?? recording.auscultationPoint ?? "unlabeled"

export {
  deriveDispositionExportValue,
  dispositionHasReviewerContent,
  observationsHaveContent,
  normalizeConfirmedBpm,
  CONFIRMED_BPM_MIN,
  CONFIRMED_BPM_MAX,
  recordingSiteLabel,
  isHeartSiteCode,
  HEART_SITE_CODES,
}
export type {
  PcgPluginReviewStatus,
  PcgPluginReview,
  PcgPluginReviews,
  PcgActResult,
  PcgBeatAwareResult,
  PcgBeatAwareStatus,
  PcgVendorEvidence,
  PcgAuscultationSite,
  HeartSiteCode,
  PcgReviewApiContract,
  PcgGuideStatus,
  PcgGuide,
  PcgRejectReason,
  PcgDispositionDecision,
  PcgAcceptedDomains,
  PcgObservationFinding,
  PcgObservationPattern,
  PcgOtherAcousticObservation,
  PcgRhythmObservation,
  PcgAcousticQuality,
  PcgReviewerObservations,
  PcgDispositionExportValue,
  PcgDispositionRequest,
  PcgRecordingDisposition,
  PcgExportRequest,
  PcgReviewExport,
  PcgReviewExportResult,
  PcgReviewExportSummary,
  PcgExportValidationErrorBody,
  PcgHeartReportStatus,
  PcgHeartReportSaveRequest,
  PcgHeartReport,
  PcgHeartReportBundleSummary,
  PcgHeartReportRelease,
  PcgHeartReportSummary,
  PcgHeartReportConflictBody,
  PcgPatientReportCard,
  PcgPatientReportDocument,
  PcgPatientReportNote,
  PcgPatientReportScale,
  PcgProviderReportDocument,
  PcgProviderReportFinding,
  PcgProviderReportQuestionnaire,
  PcgRecordingPreviewEvidence,
  PcgExamPreviewEvidence,
  PcgReviewRecordingBundle,
  PcgReviewBundle,
}
